import 'mapbox-gl/dist/mapbox-gl.css'

import * as React from 'react'
import * as UI from '@brewlogix/blx-ui'

import { Location } from 'src/types/schema/graphql'
import mapboxgl from 'mapbox-gl'
import { useRouter } from 'next/router'
import LocationCard from './LocationCard'
import { mapStyle } from '../../util/constants'

interface ILocations {
    locations: Location[]
    locationsRef?: React.Ref<HTMLDivElement>
}

const Locations: React.FC<ILocations> = ({ locations, locationsRef }) => {
    const router = useRouter()
    const mapContainer = React.useRef(null)
    const [map, setMap] = React.useState<mapboxgl.Map | null>(null)

    const goodLocations = locations.filter(
        (loc) =>
            loc?.address &&
            loc?.traits?.isOpenToPublic !== false &&
            String(loc?.traits?.status) === 'Open_for_Business'
    )
    const moreThanThreeLocations = goodLocations.length > 3

    const accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN

    // Extend the map bounds for each good location
    const bounds: any = new mapboxgl.LngLatBounds()
    goodLocations.forEach((loc) => {
        bounds.extend(new mapboxgl.LngLat(loc.address?.geo?.lon, loc.address?.geo?.lat))
    })

    React.useEffect(() => {
        if (mapContainer?.current && !map) {
            const map = new mapboxgl.Map({
                container: mapContainer.current,
                style: mapStyle,
                zoom: 3,
                accessToken,
                cooperativeGestures: true,
                center: [-99, 41],
            })
            setMap(map)
            if (goodLocations) {
                goodLocations.forEach((location, index) => {
                    const markerEl = document.createElement('div')
                    const markerIndexEl = document.createElement('h3')
                    markerIndexEl.className = 'font-lato mb-2 font-bold text-white'
                    markerIndexEl.innerText = `${index + 1}`
                    markerEl.appendChild(markerIndexEl)
                    markerEl.className = 'marker flex items-center justify-center'
                    markerEl.style.backgroundImage = `url('../../assets/BrandMap-LocationMarker.svg')`
                    markerEl.style.width = '33px'
                    markerEl.style.height = '40px'
                    markerEl.style.backgroundSize = '100%'
                    new mapboxgl.Marker(markerEl)
                        .setLngLat([location?.address?.geo?.lon, location?.address?.geo?.lat])
                        .addTo(map)
                })
            }
            map?.fitBounds(bounds, { padding: 50, duration: 5000 })
        }

        return () => {
            map?.remove()
        }
    }, [mapContainer.current])

    const handleClick = (location) =>
        router.push({
            pathname: '/brewer/[brewerSlug]/locations/[locationSlug]',
            query: { brewerSlug: location?.brand?.slug, locationSlug: location?.slug },
        })

    return (
        <div className='w-full scroll-mt-[180px]  space-y-6' id='locations' ref={locationsRef}>
            <UI.Heading text='Locations' size='h4' type='h2' customClasses='text-xl lg:text-2xl' />
            {moreThanThreeLocations ? (
                <div className='flex flex-col rounded-lg border-px border-gray-100 lg:flex-row'>
                    <div className='flex h-[364px] w-full flex-col overflow-y-scroll p-4 md:pr-0'>
                        {goodLocations.map((location, index) => (
                            <UI.Card
                                format='inline'
                                key={location?.id}
                                onClick={() => handleClick(location)}
                                image={
                                    <div className='font-lato flex h-[45px] w-[37px] items-center justify-center bg-[url(/assets/BrandMap-LocationMarker.svg)] bg-cover font-bold text-white'>
                                        <span className='mb-1 flex items-center justify-center'>
                                            {index + 1}
                                        </span>
                                    </div>
                                }
                                item={{
                                    subtitle: (
                                        <div className='flex items-center gap-x-1'>
                                            <p className='space-x-1 font-normal'>
                                                <span>{location?.address?.street}</span>,
                                                <span>{location?.address?.city}</span>,
                                                <span>{location?.address?.state}</span>
                                            </p>
                                        </div>
                                    ),
                                    title: location?.title,
                                }}
                                customClasses='flex-shrink-0 w-full border-none hover:bg-gray-50 cursor-pointer fade-2 duration-150 ease ml-0 px-0 pr-2 '
                            />
                        ))}
                    </div>
                    <div className='relative flex h-[364px] w-full overflow-hidden rounded-lg p-4'>
                        <div className='h-full w-full rounded-lg' ref={mapContainer}></div>
                    </div>
                </div>
            ) : (
                <div className='flex w-full flex-row space-x-6 overflow-x-scroll'>
                    {goodLocations.map((location) => (
                        <LocationCard location={location} key={location.id} />
                    ))}
                </div>
            )}
        </div>
    )
}

export default Locations
